import { useCallback, useEffect, useMemo, useState } from 'react';
import { useBeforeUnload, useBlocker, type BlockerFunction } from 'react-router';

import { displayUnitLabel, useUnitDisplay } from '../../app/units/UnitDisplayContext';
import { validateRequiredScenarioKinds, validateScenarioDraft } from './validation';
import {
  canonicalDemandFlow,
  DEFAULT_DELIVERY_PERIOD_S,
  requirementForKind,
} from './demand';
import './scenarios.css';

import type {
  JobRequirement,
  JobRequirementKind,
  ScenarioDraftInput,
  ScenarioFleetSettingInput,
  ScenarioFleetUnitOption,
  ScenarioNodeOption,
  ScenarioProjectSettings,
} from './types';
import { guided } from '../../app/guidance/fieldHelpRegistryCore';
import { scenarioFieldHelp } from '../../app/guidance/fieldHelpRegistry';

export interface ScenariosFeatureProps {
  existingScenarios?: ScenarioDraftInput[];
  fleetUnits?: ScenarioFleetUnitOption[];
  initialDraft: ScenarioDraftInput;
  initialSettings: ScenarioProjectSettings;
  onSave: (scenario: ScenarioDraftInput, settings: ScenarioProjectSettings) => Promise<void> | void;
  sinkNodes: ScenarioNodeOption[];
  sourceNodes: ScenarioNodeOption[];
}

function optionalNumber(value: string): number | null {
  if (value.trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function conciseNumber(value: number): string {
  return Number(value.toPrecision(12)).toString();
}
function withDefaultSchedule(
  requirement: JobRequirement,
): JobRequirement {
  return {
    ...requirement,
    delivery_period_s:
      requirement.delivery_period_s ?? DEFAULT_DELIVERY_PERIOD_S,
    expected_uptime_fraction:
      requirement.expected_uptime_fraction ?? 1,
  };
}



function fahrenheitToKelvin(value: number): number {
  return ((value - 32) * 5) / 9 + 273.15;
}

function kelvinToFahrenheit(value: number): number {
  return ((value - 273.15) * 9) / 5 + 32;
}

function cloneScenario(value: ScenarioDraftInput): ScenarioDraftInput {
  return {
    ...value,
    fleet_settings: value.fleet_settings.map((setting) => ({ ...setting })),
    sinks: value.sinks.map((condition) => ({
      ...condition,
      ...(condition.job_requirement === undefined
        ? {}
        : {
            job_requirement:
              condition.job_requirement === null
                ? null
                : withDefaultSchedule(condition.job_requirement),
          }),
    })),
    sources: value.sources.map((condition) => ({ ...condition })),
    water: { ...value.water },
  };
}

function cloneSettings(value: ScenarioProjectSettings): ScenarioProjectSettings {
  return { ...value };
}

function scenarioMatches(left: ScenarioDraftInput, right: ScenarioDraftInput): boolean {
  const leftFleet = [...left.fleet_settings].sort((a, b) => a.fleet_unit_id.localeCompare(b.fleet_unit_id));
  const rightFleet = [...right.fleet_settings].sort((a, b) => a.fleet_unit_id.localeCompare(b.fleet_unit_id));
  const leftSinks = [...left.sinks].sort((a, b) => a.sink_node_id.localeCompare(b.sink_node_id));
  const rightSinks = [...right.sinks].sort((a, b) => a.sink_node_id.localeCompare(b.sink_node_id));
  const leftSources = [...left.sources].sort((a, b) => a.source_node_id.localeCompare(b.source_node_id));
  const rightSources = [...right.sources].sort((a, b) => a.source_node_id.localeCompare(b.source_node_id));

  return (
    left.id === right.id &&
    left.kind === right.kind &&
    left.name === right.name &&
    left.required === right.required &&
    left.weight === right.weight &&
    left.water.density_kg_m3 === right.water.density_kg_m3 &&
    left.water.kinematic_viscosity_m2_s === right.water.kinematic_viscosity_m2_s &&
    left.water.temperature_k === right.water.temperature_k &&
    left.water.vapor_pressure_absolute_pa === right.water.vapor_pressure_absolute_pa &&
    leftFleet.length === rightFleet.length &&
    leftFleet.every(
      (setting, index) =>
        setting.available === rightFleet[index]?.available &&
        setting.driver_power_derate_fraction === rightFleet[index]?.driver_power_derate_fraction &&
        setting.fleet_unit_id === rightFleet[index]?.fleet_unit_id &&
        setting.maximum_engine_speed_rpm === rightFleet[index]?.maximum_engine_speed_rpm,
    ) &&
    leftSinks.length === rightSinks.length &&
    leftSinks.every(
      (condition, index) =>
        condition.minimum_residual_pressure_gauge_pa === rightSinks[index]?.minimum_residual_pressure_gauge_pa &&
        JSON.stringify(condition.job_requirement ?? null) ===
          JSON.stringify(rightSinks[index]?.job_requirement ?? null) &&
        condition.required_flow_m3_s === rightSinks[index]?.required_flow_m3_s &&
        condition.sink_node_id === rightSinks[index]?.sink_node_id,
    ) &&
    leftSources.length === rightSources.length &&
    leftSources.every(
      (condition, index) =>
        condition.hydraulic_head_m === rightSources[index]?.hydraulic_head_m &&
        condition.maximum_withdrawal_flow_m3_s === rightSources[index]?.maximum_withdrawal_flow_m3_s &&
        condition.source_node_id === rightSources[index]?.source_node_id,
    )
  );
}

function settingsMatch(left: ScenarioProjectSettings, right: ScenarioProjectSettings): boolean {
  return (
    left.component_pressure_margin_fraction === right.component_pressure_margin_fraction &&
    left.driver_power_reserve_fraction === right.driver_power_reserve_fraction &&
    left.headloss_model === right.headloss_model &&
    left.minimum_profile_pressure_gauge_pa === right.minimum_profile_pressure_gauge_pa &&
    left.npsh_margin_m === right.npsh_margin_m &&
    left.safety_acknowledged === right.safety_acknowledged &&
    left.terrain_accuracy_acknowledged === right.terrain_accuracy_acknowledged
  );
}

export function ScenariosFeature({ existingScenarios = [], fleetUnits = [], initialDraft, initialSettings, onSave, sinkNodes, sourceNodes }: ScenariosFeatureProps) {
  const { formatSiValue, toDisplayValue, toSiValue, units } = useUnitDisplay();
  const [scenario, setScenario] = useState(() => cloneScenario(initialDraft));
  const [settings, setSettings] = useState(() => cloneSettings(initialSettings));
  const [savedScenario, setSavedScenario] = useState(() => cloneScenario(initialDraft));
  const [savedSettings, setSavedSettings] = useState(() => cloneSettings(initialSettings));
  const [newKind, setNewKind] = useState<ScenarioDraftInput['kind']>('peak');
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveNotice, setSaveNotice] = useState<string | null>(null);
  const issues = useMemo(() => validateScenarioDraft(scenario, sourceNodes, sinkNodes, settings, fleetUnits), [fleetUnits, scenario, settings, sinkNodes, sourceNodes]);
  const setIssues = validateRequiredScenarioKinds([...existingScenarios.filter((item) => item.id !== scenario.id), scenario]);
  const fleetOverrideCount = scenario.fleet_settings.filter((setting) => !setting.available || setting.maximum_engine_speed_rpm !== null || setting.driver_power_derate_fraction > 0).length;
  const scenarioDirty = !scenarioMatches(scenario, savedScenario);
  const settingsDirty = !settingsMatch(settings, savedSettings);
  const unsafeToLeave = scenarioDirty || settingsDirty || busy;

  useBeforeUnload(
    useCallback(
      (event) => {
        if (unsafeToLeave) {
          event.preventDefault();
          event.returnValue = '';
        }
      },
      [unsafeToLeave],
    ),
  );
  const navigationBlocker = useBlocker(
    useCallback<BlockerFunction>(
      ({ currentLocation, nextLocation }) => unsafeToLeave && currentLocation.pathname !== nextLocation.pathname,
      [unsafeToLeave],
    ),
  );

  useEffect(() => {
    if (navigationBlocker.state !== 'blocked') return;
    const leave = window.confirm(busy ? 'A scenario save is still running. Leave Scenarios?' : 'Leave Scenarios and discard unsaved changes?');
    if (leave) {
      navigationBlocker.proceed();
    } else {
      navigationBlocker.reset();
    }
  }, [busy, navigationBlocker]);

  useEffect(() => {
    setSaveNotice(null);
  }, [scenario, settings]);

  const startNewScenario = () => {
    if (scenarioDirty && !window.confirm('Start a new scenario? Unsaved changes will be copied, not saved to the current scenario.')) return;
    const identifier = globalThis.crypto?.randomUUID?.() ?? `scenario-${Date.now()}`;
    const title = newKind[0]!.toUpperCase() + newKind.slice(1);
    setScenario((current) => ({
      ...cloneScenario(current),
      id: identifier,
      kind: newKind,
      name: `${title} operation`,
      required: newKind !== 'contingency',
    }));
    setSaveError(null);
  };

  const resetSafetyAcknowledgment = (update: Partial<ScenarioProjectSettings>) => {
    setSettings((current) => ({
      ...current,
      ...update,
      safety_acknowledged: false,
    }));
  };

  const updateSource = (identifier: string, field: 'hydraulic_head_m' | 'maximum_withdrawal_flow_m3_s', value: number | null) => {
    setScenario((current) => ({
      ...current,
      sources: current.sources.map((condition) => (condition.source_node_id === identifier ? { ...condition, [field]: value } : condition)),
    }));
  };

  const updateSink = (identifier: string, field: 'minimum_residual_pressure_gauge_pa' | 'required_flow_m3_s', value: number | null) => {
    setScenario((current) => ({
      ...current,
      sinks: current.sinks.map((condition) => (condition.sink_node_id === identifier ? { ...condition, [field]: value } : condition)),
    }));
  };

  const updateJobRequirement = (
    identifier: string,
    jobRequirement: JobRequirement,
  ) => {
    setScenario((current) => ({
      ...current,
      sinks: current.sinks.map((condition) =>
        condition.sink_node_id === identifier
          ? {
              ...condition,
              job_requirement: jobRequirement,
              required_flow_m3_s: canonicalDemandFlow(jobRequirement),
            }
          : condition,
      ),
    }));
  };
  const updateWater = (update: Partial<ScenarioDraftInput['water']>) => {
    setScenario((current) => ({
      ...current,
      water: { ...current.water, ...update },
    }));
    setSettings((current) => ({ ...current, safety_acknowledged: false }));
  };

  const updateFleetSetting = (fleetUnitId: string, update: Partial<ScenarioFleetSettingInput>) => {
    setScenario((current) => {
      const existing = current.fleet_settings.find((item) => item.fleet_unit_id === fleetUnitId);
      const setting: ScenarioFleetSettingInput = {
        available: true,
        driver_power_derate_fraction: 0,
        fleet_unit_id: fleetUnitId,
        maximum_engine_speed_rpm: null,
        ...existing,
        ...update,
      };
      return {
        ...current,
        fleet_settings: [...current.fleet_settings.filter((item) => item.fleet_unit_id !== fleetUnitId), setting],
      };
    });
  };

  const save = async () => {
    if (issues.length > 0) return;
    setBusy(true);
    setSaveError(null);
    setSaveNotice(null);
    try {
      await onSave(scenario, settings);
      setSavedScenario(cloneScenario(scenario));
      setSavedSettings(cloneSettings(settings));
      setSaveNotice('Scenario saved.');
    } catch (caught) {
      setSaveError(caught instanceof Error ? caught.message : 'The scenario could not be saved.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-labelledby="scenario-editor-title" className="scenario-feature">
      <h1 id="scenario-editor-title">Scenarios</h1>

      <fieldset disabled={busy}>
        <legend>Scenario</legend>
        <label>
          Scenario to edit
          <select
            {...guided(scenarioFieldHelp.selectedScenario)}
            onChange={(event) => {
              const selected = existingScenarios.find((item) => item.id === event.currentTarget.value);
              if (selected !== undefined) {
                if (scenarioDirty && !window.confirm('Discard unsaved changes to this scenario?')) return;
                const nextScenario = cloneScenario(selected);
                setScenario(nextScenario);
                setSavedScenario(cloneScenario(nextScenario));
                setSaveError(null);
              }
            }}
            value={scenario.id}
          >
            {existingScenarios.some((item) => item.id === scenario.id) ? null : <option value={scenario.id}>{scenario.name} · unsaved</option>}
            {existingScenarios.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name} · {item.kind}
              </option>
            ))}
          </select>
        </label>
        <details className="scenario-options">
          <summary>New scenario</summary>
          <label>
            New scenario kind
            <select {...guided(scenarioFieldHelp.newKind)} onChange={(event) => setNewKind(event.currentTarget.value as ScenarioDraftInput['kind'])} value={newKind}>
              <option value="normal">Normal</option>
              <option value="peak">Peak</option>
              <option value="contingency">Contingency</option>
            </select>
          </label>
          <button onClick={startNewScenario} type="button">
            New from current
          </button>
        </details>
      </fieldset>

      <fieldset disabled={busy}>
        <legend>Scenario identity</legend>
        <label>
          Scenario name
          <input
            {...guided(scenarioFieldHelp.name)}
            onChange={(event) => {
              const name = event.currentTarget.value;
              setScenario((current) => ({ ...current, name }));
            }}
            value={scenario.name}
          />
        </label>
        <label>
          Scenario kind
          <select
            {...guided(scenarioFieldHelp.kind)}
            onChange={(event) => {
              const kind = event.currentTarget.value as ScenarioDraftInput['kind'];
              setScenario((current) => ({ ...current, kind }));
            }}
            value={scenario.kind}
          >
            <option value="normal">Normal</option>
            <option value="peak">Peak</option>
            <option value="contingency">Contingency</option>
          </select>
        </label>
        <label>
          <input
            {...guided(scenarioFieldHelp.required)}
            checked={scenario.required}
            onChange={(event) => {
              const required = event.currentTarget.checked;
              setScenario((current) => ({ ...current, required }));
            }}
            type="checkbox"
          />
          Required design scenario
        </label>
      </fieldset>

      <fieldset disabled={busy}>
        <legend>Sources</legend>
        {sourceNodes.map((node) => {
          const condition = scenario.sources.find((candidate) => candidate.source_node_id === node.id);
          return (
            <div className="scenario-condition" key={node.id}>
              <h2>{node.name}</h2>
              <label>
                Hydraulic head ({displayUnitLabel(units.head)})
                <input
                  {...guided(scenarioFieldHelp.sourceHead, node.id)}
                  aria-label={`Hydraulic head for ${node.name} (${displayUnitLabel(units.head)})`}
                  onChange={(event) => {
                    const value = optionalNumber(event.currentTarget.value);
                    updateSource(node.id, 'hydraulic_head_m', value === null ? null : toSiValue(value, 'head'));
                  }}
                  type="number"
                  value={condition?.hydraulic_head_m === null || condition === undefined ? '' : conciseNumber(toDisplayValue(condition.hydraulic_head_m, 'head'))}
                />
              </label>
              <label>
                Maximum withdrawal ({displayUnitLabel(units.flowRate)})
                <input
                  {...guided(scenarioFieldHelp.sourceWithdrawal, node.id)}
                  aria-label={`Maximum withdrawal for ${node.name} (${displayUnitLabel(units.flowRate)})`}
                  min="0"
                  onChange={(event) => {
                    const value = optionalNumber(event.currentTarget.value);
                    updateSource(node.id, 'maximum_withdrawal_flow_m3_s', value === null ? null : toSiValue(value, 'flowRate'));
                  }}
                  type="number"
                  value={condition?.maximum_withdrawal_flow_m3_s === null || condition === undefined ? '' : conciseNumber(toDisplayValue(condition.maximum_withdrawal_flow_m3_s, 'flowRate'))}
                />
              </label>
            </div>
          );
        })}
      </fieldset>

      <fieldset disabled={busy}>
        <legend>Deliveries</legend>
        {sinkNodes.map((node) => {
          const condition = scenario.sinks.find((candidate) => candidate.sink_node_id === node.id);
          const jobRequirement =
            condition?.job_requirement === undefined ||
            condition.job_requirement === null
              ? requirementForKind(
                  'direct_flow',
                  condition?.required_flow_m3_s ?? null,
                )
              : withDefaultSchedule(condition.job_requirement);
          return (
            <div className="scenario-condition" key={node.id}>
              <h2>{node.name}</h2>
              <label>
                Requirement
                <select
                  {...guided(scenarioFieldHelp.requirementKind, node.id)}
                  aria-label={`Requirement for ${node.name}`}
                  onChange={(event) =>
                    updateJobRequirement(
                      node.id,
                      requirementForKind(
                        event.currentTarget.value as JobRequirementKind,
                        condition?.required_flow_m3_s ?? null,
                        jobRequirement,
                      ),
                    )
                  }
                  value={jobRequirement.kind}
                >
                  <option value="direct_flow">Flow rate</option>
                  <option value="barrels_per_minute">Barrels/minute</option>
                  <option value="barrels_per_hour">Barrels/hour</option>
                  <option value="total_barrels">Total barrels</option>
                </select>
              </label>
              {jobRequirement.kind === 'direct_flow' ? (
                <label>
                  Required flow ({displayUnitLabel(units.flowRate)})
                  <input
                    {...guided(scenarioFieldHelp.sinkFlow, node.id)}
                    aria-label={`Required flow for ${node.name} (${displayUnitLabel(units.flowRate)})`}
                    min="0"
                    onChange={(event) => {
                      const value = optionalNumber(event.currentTarget.value);
                      updateJobRequirement(node.id, {
                        ...jobRequirement,
                        direct_flow_m3_s:
                          value === null ? null : toSiValue(value, 'flowRate'),
                        kind: 'direct_flow',
                      });
                    }}
                    type="number"
                    value={
                      jobRequirement.direct_flow_m3_s === null
                        ? ''
                        : conciseNumber(
                            toDisplayValue(
                              jobRequirement.direct_flow_m3_s,
                              'flowRate',
                            ),
                          )
                    }
                  />
                </label>
              ) : null}
              {jobRequirement.kind === 'barrels_per_minute' ? (
                <label>
                  Barrels per minute
                  <input
                    {...guided(scenarioFieldHelp.barrelsPerMinute, node.id)}
                    aria-label={`Barrels per minute for ${node.name}`}
                    min="0"
                    onChange={(event) =>
                      updateJobRequirement(node.id, {
                        ...jobRequirement,
                        barrels_per_minute: optionalNumber(
                          event.currentTarget.value,
                        ),
                        kind: 'barrels_per_minute',
                      })
                    }
                    type="number"
                    value={jobRequirement.barrels_per_minute ?? ''}
                  />
                </label>
              ) : null}
              {jobRequirement.kind === 'barrels_per_hour' ? (
                <label>
                  Barrels per hour
                  <input
                    {...guided(scenarioFieldHelp.barrelsPerHour, node.id)}
                    aria-label={`Barrels per hour for ${node.name}`}
                    min="0"
                    onChange={(event) =>
                      updateJobRequirement(node.id, {
                        ...jobRequirement,
                        barrels_per_hour: optionalNumber(
                          event.currentTarget.value,
                        ),
                        kind: 'barrels_per_hour',
                      })
                    }
                    type="number"
                    value={jobRequirement.barrels_per_hour ?? ''}
                  />
                </label>
              ) : null}
              {jobRequirement.kind === 'total_barrels' ? (
                  <label>
                    Total barrels
                    <input
                      {...guided(scenarioFieldHelp.totalBarrels, node.id)}
                      aria-label={`Total barrels for ${node.name}`}
                      min="0"
                      onChange={(event) =>
                        updateJobRequirement(node.id, {
                          ...jobRequirement,
                          total_barrels: optionalNumber(
                            event.currentTarget.value,
                          ),
                        })
                      }
                      type="number"
                      value={jobRequirement.total_barrels ?? ''}
                    />
                  </label>
              ) : null}
              <label>
                    Delivery period (hours)
                    <input
                      {...guided(scenarioFieldHelp.deliveryPeriod, node.id)}
                      aria-label={`Delivery period for ${node.name} (hours)`}
                      min="0"
                      onChange={(event) => {
                        const value = optionalNumber(event.currentTarget.value);
                        updateJobRequirement(node.id, {
                          ...jobRequirement,
                          delivery_period_s: value === null ? null : value * 3600,
                        });
                      }}
                      type="number"
                      value={
                        jobRequirement.delivery_period_s === null
                          ? ''
                          : jobRequirement.delivery_period_s / 3600
                      }
                    />
                  </label>
                  <label>
                    Expected uptime (%)
                    <input
                      {...guided(scenarioFieldHelp.expectedUptime, node.id)}
                      aria-label={`Expected uptime for ${node.name} (%)`}
                      max="100"
                      min="0"
                      onChange={(event) => {
                        const value = optionalNumber(event.currentTarget.value);
                        updateJobRequirement(node.id, {
                          ...jobRequirement,
                          expected_uptime_fraction:
                            value === null ? null : value / 100,
                        });
                      }}
                      type="number"
                      value={
                        jobRequirement.expected_uptime_fraction === null
                          ? ''
                          : jobRequirement.expected_uptime_fraction * 100
                      }
                    />
                  </label>
              {jobRequirement.kind === 'direct_flow' ||
              condition?.required_flow_m3_s === null ||
              condition === undefined ? null : (
                <p>
                  Required flow:{' '}
                  {formatSiValue(condition.required_flow_m3_s, 'flowRate')}
                </p>
              )}
              <label>
                Minimum pressure ({displayUnitLabel(units.pressure)})
                <input
                  {...guided(scenarioFieldHelp.sinkPressure, node.id)}
                  aria-label={`Minimum pressure for ${node.name} (${displayUnitLabel(units.pressure)})`}
                  min="0"
                  onChange={(event) => {
                    const value = optionalNumber(event.currentTarget.value);
                    updateSink(node.id, 'minimum_residual_pressure_gauge_pa', value === null ? null : toSiValue(value, 'pressure'));
                  }}
                  type="number"
                  value={condition?.minimum_residual_pressure_gauge_pa === null || condition === undefined ? '' : conciseNumber(toDisplayValue(condition.minimum_residual_pressure_gauge_pa, 'pressure'))}
                />
              </label>
            </div>
          );
        })}
      </fieldset>

      <details className="scenario-options">
        <summary>Water properties · {conciseNumber(kelvinToFahrenheit(scenario.water.temperature_k))} °F</summary>
        <fieldset disabled={busy}>
          <legend className="visually-hidden">Water properties</legend>
          <p>Defaults: clean water at 60 °F.</p>
          <label>
            Temperature (°F)
            <input
              {...guided(scenarioFieldHelp.temperature)}
              max="212"
              min="32"
              onChange={(event) =>
                updateWater({
                  temperature_k: fahrenheitToKelvin(event.currentTarget.valueAsNumber),
                })
              }
              step="0.1"
              type="number"
              value={conciseNumber(kelvinToFahrenheit(scenario.water.temperature_k))}
            />
          </label>
          <label>
            Specific gravity (water = 1.0)
            <input
              {...guided(scenarioFieldHelp.specificGravity)}
              min="0.001"
              onChange={(event) =>
                updateWater({
                  density_kg_m3: event.currentTarget.valueAsNumber * 1000,
                })
              }
              step="0.001"
              type="number"
              value={conciseNumber(scenario.water.density_kg_m3 / 1000)}
            />
          </label>
          <label>
            Kinematic viscosity (cSt)
            <input
              {...guided(scenarioFieldHelp.viscosity)}
              min="0.001"
              onChange={(event) =>
                updateWater({
                  kinematic_viscosity_m2_s: event.currentTarget.valueAsNumber * 1e-6,
                })
              }
              step="0.001"
              type="number"
              value={conciseNumber(scenario.water.kinematic_viscosity_m2_s * 1e6)}
            />
          </label>
          <label>
            Vapor pressure, absolute ({displayUnitLabel(units.pressure)})
            <input
              {...guided(scenarioFieldHelp.vaporPressure)}
              min="0"
              onChange={(event) =>
                updateWater({
                  vapor_pressure_absolute_pa: toSiValue(event.currentTarget.valueAsNumber, 'pressure'),
                })
              }
              step="0.001"
              type="number"
              value={conciseNumber(toDisplayValue(scenario.water.vapor_pressure_absolute_pa, 'pressure'))}
            />
          </label>
        </fieldset>
      </details>

      <details className="scenario-options">
        <summary>
          Outages and derates
          {fleetOverrideCount === 0 ? '' : ` · ${fleetOverrideCount} set`}
        </summary>
        <fieldset disabled={busy}>
          <legend className="visually-hidden">Outages and derates</legend>
          <p>Set unit outages, RPM caps, or power reductions.</p>
          {fleetUnits.length === 0 ? (
            <p>No active fleet units are available. Add named physical units in Fleet.</p>
          ) : (
            fleetUnits.map((unit) => {
              const setting = scenario.fleet_settings.find((item) => item.fleet_unit_id === unit.id);
              const baseAvailable = unit.availability === 'available';
              return (
                <div className="scenario-condition" key={unit.id}>
                  <h2>
                    {unit.physical_identifier} · {unit.name}
                  </h2>
                  <p>
                    {unit.template_name} · base state {unit.availability} · maximum {unit.maximum_engine_speed_rpm} RPM
                  </p>
                  <label>
                    <input
                      {...guided(scenarioFieldHelp.unitAvailable, unit.id)}
                      aria-label={`Available in this scenario for ${unit.physical_identifier}`}
                      checked={baseAvailable && (setting?.available ?? true)}
                      disabled={!baseAvailable}
                      onChange={(event) =>
                        updateFleetSetting(unit.id, {
                          available: event.currentTarget.checked,
                        })
                      }
                      type="checkbox"
                    />
                    Available in this scenario
                  </label>
                  {!baseAvailable ? <p role="note">Restore the base fleet state in Fleet before this scenario can use the unit.</p> : null}
                  <label>
                    Optional maximum engine RPM
                    <input
                      {...guided(scenarioFieldHelp.unitMaxRpm, unit.id)}
                      aria-label={`Optional maximum engine RPM for ${unit.physical_identifier}`}
                      max={unit.maximum_engine_speed_rpm}
                      min="1"
                      onChange={(event) =>
                        updateFleetSetting(unit.id, {
                          maximum_engine_speed_rpm: optionalNumber(event.currentTarget.value),
                        })
                      }
                      placeholder={`Base maximum ${unit.maximum_engine_speed_rpm}`}
                      type="number"
                      value={setting?.maximum_engine_speed_rpm ?? ''}
                    />
                  </label>
                  <label>
                    Scenario driver-power reduction (%)
                    <input
                      {...guided(scenarioFieldHelp.unitPowerReduction, unit.id)}
                      aria-label={`Scenario driver-power reduction for ${unit.physical_identifier} (%)`}
                      max="99.9"
                      min="0"
                      onChange={(event) =>
                        updateFleetSetting(unit.id, {
                          driver_power_derate_fraction: event.currentTarget.valueAsNumber / 100,
                        })
                      }
                      step="0.1"
                      type="number"
                      value={conciseNumber((setting?.driver_power_derate_fraction ?? 0) * 100)}
                    />
                  </label>
                </div>
              );
            })
          )}
        </fieldset>
      </details>

      <fieldset disabled={busy}>
        <legend>Safety limits</legend>
        <label>
          Friction method
          <select
            {...guided(scenarioFieldHelp.frictionMethod)}
            onChange={(event) =>
              resetSafetyAcknowledgment({
                headloss_model: event.currentTarget.value as ScenarioProjectSettings['headloss_model'],
              })
            }
            value={settings.headloss_model}
          >
            <option value="darcy_weisbach">Darcy–Weisbach</option>
            <option value="hazen_williams">Hazen–Williams</option>
          </select>
        </label>
        <label>
          Component pressure margin (%)
          <input
            {...guided(scenarioFieldHelp.pressureMargin)}
            max="99.9"
            min="0"
            onChange={(event) =>
              resetSafetyAcknowledgment({
                component_pressure_margin_fraction: event.currentTarget.valueAsNumber / 100,
              })
            }
            step="0.01"
            type="number"
            value={settings.component_pressure_margin_fraction * 100}
          />
        </label>
        <label>
          Driver power reserve (%)
          <input
            {...guided(scenarioFieldHelp.powerReserve)}
            max="99.9"
            min="0"
            onChange={(event) =>
              resetSafetyAcknowledgment({
                driver_power_reserve_fraction: event.currentTarget.valueAsNumber / 100,
              })
            }
            step="0.01"
            type="number"
            value={settings.driver_power_reserve_fraction * 100}
          />
        </label>
        <label>
          NPSH margin ({displayUnitLabel(units.head)})
          <input
            {...guided(scenarioFieldHelp.npshMargin)}
            min="0"
            onChange={(event) =>
              resetSafetyAcknowledgment({
                npsh_margin_m: toSiValue(event.currentTarget.valueAsNumber, 'head'),
              })
            }
            step="0.001"
            type="number"
            value={conciseNumber(toDisplayValue(settings.npsh_margin_m, 'head'))}
          />
        </label>
        <label>
          Minimum internal route gauge pressure ({displayUnitLabel(units.pressure)}; optional)
          <input
            {...guided(scenarioFieldHelp.profilePressure)}
            min="0"
            onChange={(event) => {
              const value = optionalNumber(event.currentTarget.value);
              resetSafetyAcknowledgment({
                minimum_profile_pressure_gauge_pa: value === null ? null : toSiValue(value, 'pressure'),
              });
            }}
            placeholder="Not configured"
            step="0.001"
            type="number"
            value={settings.minimum_profile_pressure_gauge_pa === null ? '' : conciseNumber(toDisplayValue(settings.minimum_profile_pressure_gauge_pa, 'pressure'))}
          />
        </label>
        <p>Checks the full route profile.</p>
        <label>
          <input
            {...guided(scenarioFieldHelp.safetyAcknowledgment)}
            checked={settings.safety_acknowledged}
            onChange={(event) => {
              const acknowledged = event.currentTarget.checked;
              setSettings((current) => ({
                ...current,
                safety_acknowledged: acknowledged,
              }));
            }}
            type="checkbox"
          />
          I reviewed the safety limits and fluid properties
        </label>
        <label>
          <input
            {...guided(scenarioFieldHelp.terrainAcknowledgment)}
            checked={settings.terrain_accuracy_acknowledged}
            onChange={(event) => {
              const acknowledged = event.currentTarget.checked;
              setSettings((current) => ({
                ...current,
                terrain_accuracy_acknowledged: acknowledged,
              }));
            }}
            type="checkbox"
          />
          I reviewed the planning-grade terrain limits
        </label>
      </fieldset>

      {issues.length === 0 ? (
        <p role="status">{saveNotice ?? 'Ready to save.'}</p>
      ) : (
        <ul aria-label="Scenario validation issues">
          {issues.map((issue) => (
            <li key={issue.code}>{issue.message}</li>
          ))}
        </ul>
      )}
      {setIssues.length === 0 ? null : (
        <ul aria-label="Scenario set validation issues">
          {setIssues.map((issue) => (
            <li key={`${issue.code}-${issue.message}`}>{issue.message}</li>
          ))}
        </ul>
      )}
      {saveError === null ? null : <p role="alert">{saveError}</p>}
      <button disabled={busy || issues.length > 0} onClick={() => void save()} type="button">
        Save scenario
      </button>
    </section>
  );
}
